import { create } from 'zustand'
import type { ImageDTO, MediaCounts, MediaFilter, MediaPageDTO, MediaSort } from '@shared/types'
import { IMAGES_PER_PAGE } from '@shared/constants'
import { api, errorMessage } from '@/lib/api'

/** The only fields `PATCH /media/:imageId` accepts. */
export interface ImagePatch {
  editedAlt?: string | null
  approved?: boolean
}

interface MediaState {
  siteId: string | null
  items: ImageDTO[]
  counts: MediaCounts | null
  page: number
  perPage: number
  total: number
  totalPages: number
  filter: MediaFilter
  sort: MediaSort
  query: string
  loading: boolean
  error: string | null
  selected: Set<string>

  open: (siteId: string) => Promise<void>
  reload: () => Promise<void>
  setPage: (n: number) => void
  setFilter: (f: MediaFilter) => void
  setSort: (s: MediaSort) => void
  setQuery: (q: string) => void
  patchImage: (id: string, patch: ImagePatch) => Promise<void>
  applyServerPatch: (id: string, patch: Partial<ImageDTO>) => void
  toggleSelect: (id: string) => void
  selectAll: () => void
  clearSelection: () => void
}

const SEARCH_DEBOUNCE_MS = 280

/**
 * Monotonic request id. Kept out of the store: it changes on every fetch and no
 * component should re-render for it.
 */
let requestSeq = 0
let searchTimer: ReturnType<typeof setTimeout> | null = null

function cancelPendingSearch(): void {
  if (searchTimer === null) return
  clearTimeout(searchTimer)
  searchTimer = null
}

export const useMediaStore = create<MediaState>((set, get) => ({
  siteId: null,
  items: [],
  counts: null,
  page: 1,
  perPage: IMAGES_PER_PAGE,
  total: 0,
  totalPages: 0,
  filter: 'all',
  sort: 'newest',
  query: '',
  loading: false,
  error: null,
  selected: new Set(),

  open: async (siteId) => {
    if (get().siteId === siteId) {
      // Same site (a remount, or StrictMode's double effect) — keep the view the
      // user had and just resync it.
      await get().reload()
      return
    }
    cancelPendingSearch()
    set({
      siteId,
      items: [],
      counts: null,
      page: 1,
      total: 0,
      totalPages: 0,
      filter: 'all',
      sort: 'newest',
      query: '',
      selected: new Set(),
      loading: true,
      error: null
    })
    await get().reload()
  },

  reload: async () => {
    const { siteId, page, perPage, filter, sort, query } = get()
    if (!siteId) return

    const seq = ++requestSeq
    set({ loading: true, error: null })

    const params = new URLSearchParams({
      page: String(page),
      perPage: String(perPage),
      filter,
      sort
    })
    const trimmed = query.trim()
    if (trimmed) params.set('q', trimmed)

    try {
      const data = await api.get<MediaPageDTO>(`/sites/${siteId}/media?${params.toString()}`)
      // Typing in the search box outruns the network; only the newest reply counts.
      if (seq !== requestSeq) return
      set({
        items: data.items,
        counts: data.counts,
        page: data.page,
        perPage: data.perPage,
        total: data.total,
        totalPages: data.totalPages,
        loading: false
      })
    } catch (error) {
      if (seq !== requestSeq) return
      set({ loading: false, error: errorMessage(error) })
    }
  },

  setPage: (n) => {
    const next = Math.max(1, Math.floor(n))
    if (next === get().page) return
    cancelPendingSearch()
    // Selection is page-scoped: carrying it across pages hides selected items
    // behind a paginator, and the batch bar would then act on the invisible.
    set({ page: next, selected: new Set() })
    void get().reload()
  },

  setFilter: (f) => {
    if (f === get().filter) return
    cancelPendingSearch()
    set({ filter: f, page: 1, selected: new Set() })
    void get().reload()
  },

  setSort: (s) => {
    if (s === get().sort) return
    cancelPendingSearch()
    set({ sort: s, page: 1, selected: new Set() })
    void get().reload()
  },

  setQuery: (q) => {
    if (q === get().query) return
    // The value lands immediately so a store-driven input stays responsive; only
    // the round trip waits.
    set({ query: q, page: 1, selected: new Set() })
    cancelPendingSearch()
    searchTimer = setTimeout(() => {
      searchTimer = null
      void get().reload()
    }, SEARCH_DEBOUNCE_MS)
  },

  patchImage: async (id, patch) => {
    const previous = get().items.find((item) => item.id === id)
    if (!previous) return

    const optimistic: Partial<ImageDTO> = { ...patch }
    // The server clears approval whenever the text changes; mirroring it here
    // stops the checkbox from flickering back on after the response lands.
    if (patch.editedAlt !== undefined && patch.approved === undefined) optimistic.approved = false

    set((state) => ({
      items: state.items.map((item) => (item.id === id ? { ...item, ...optimistic } : item))
    }))

    try {
      const updated = await api.patch<ImageDTO>(`/media/${id}`, patch)
      set((state) => ({
        items: state.items.map((item) => (item.id === id ? updated : item))
      }))
    } catch (error) {
      // Restore the exact row we had, not a merge — a partial rollback would
      // leave the UI in a state the server never held.
      set((state) => ({
        items: state.items.map((item) => (item.id === id ? previous : item))
      }))
      throw error
    }
  },

  applyServerPatch: (id, patch) => {
    if (!get().items.some((item) => item.id === id)) return
    set((state) => ({
      items: state.items.map((item) => (item.id === id ? { ...item, ...patch } : item))
    }))
  },

  toggleSelect: (id) => {
    set((state) => {
      const next = new Set(state.selected)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { selected: next }
    })
  },

  // Only the current page is in memory, so "all" means all of it. Acting on
  // everything that matches the filter is a server-side operation.
  selectAll: () => {
    set((state) => ({ selected: new Set(state.items.map((item) => item.id)) }))
  },

  clearSelection: () => {
    set({ selected: new Set() })
  }
}))
